<template>
    <div class="admin-site-audit-log">
        <div class="admin-site-audit-log__panel">
            <div class="admin-site-audit-log__panel-head">
                <i class="material-icons admin-site-audit-log__head-icon">history</i>
                <span class="admin-site-audit-log__head-title">{{ $t("auto.adminSiteAuditLog") }}</span>
            </div>
            <NewSupsisTable
                :columns="tableColumns"
                :data="rows"
                :loading="loading"
                :zebra="true"
                :show-column-add="false"
                :sortable-columns="false"
                :resizable="true"
                :min-column-width="88"
                row-key="_id"
                class="admin-site-audit-log__table"
            >
                <template #cell-createdAt="slotProps">
                    <div class="cell-datetime">
                        <span class="cell-datetime__date">{{ formatDate(slotProps.row.createdAt) }}</span>
                        <span class="cell-datetime__time">{{ formatTime(slotProps.row.createdAt) }}</span>
                    </div>
                </template>
                <template #cell-summary="slotProps">
                    <a-tag :color="summaryTagColor(slotProps.row)" class="cell-summary-tag">
                        {{ eventSummary(slotProps.row) }}
                    </a-tag>
                </template>
                <template #cell-description="slotProps">
                    <p class="cell-description">{{ descriptionText(slotProps.row) }}</p>
                </template>
                <template #cell-actor="slotProps">
                    <span class="cell-actor">{{ actorLabel(slotProps.row) }}</span>
                </template>
                <template #empty>
                    <span class="cell-empty">{{ $t("auditLogs.noActivityYet") }}</span>
                </template>
            </NewSupsisTable>
            <div class="admin-site-audit-log__pager" v-if="totalCount > 0">
                <a-pagination
                    :current="cursor.page"
                    :page-size="cursor.perPage"
                    :total="totalCount"
                    :show-size-changer="true"
                    :page-size-options="['10', '20', '50', '100']"
                    @change="onPageChange"
                    @showSizeChange="onSizeChange"
                />
            </div>
        </div>
    </div>
</template>

<script>
import * as _ from "lodash";
import moment from "moment";
import NewSupsisTable from "../../components/common/NewSupsisTable.vue";
import { getSiteAuditLog } from "../../api/site";

const SITE_STATUS_ACTIVITY_TYPES = ["site-suspended", "site-dead", "site-enabled"];

export default {
    name: "AdminSiteAuditLog",
    components: {
        NewSupsisTable,
    },
    props: {
        siteId: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            loading: false,
            rows: [],
            totalCount: 0,
            cursor: {
                page: 1,
                perPage: 20,
            },
        };
    },
    computed: {
        tableColumns() {
            return [
                {
                    key: "createdAt",
                    title: this.$t("settings.dateAndTime"),
                    visible: true,
                    width: 168,
                },
                {
                    key: "summary",
                    title: this.$t("auto.adminSiteAuditColumnSummary"),
                    visible: true,
                    width: 260,
                },
                {
                    key: "description",
                    title: this.$t("auto.adminSiteAuditColumnDescription"),
                    visible: true,
                    width: 360,
                },
                {
                    key: "actor",
                    title: this.$t("auto.adminSiteAuditColumnUser"),
                    visible: true,
                    width: 260,
                },
            ];
        },
    },
    watch: {
        siteId: {
            handler() {
                this.resetAndFetch();
            },
        },
    },
    mounted() {
        this.resetAndFetch();
    },
    methods: {
        onPageChange(page) {
            this.cursor.page = page;
            this.fetchPage();
        },
        onSizeChange(current, size) {
            this.cursor.page = 1;
            this.cursor.perPage = size;
            this.fetchPage();
        },
        typeLabel(type) {
            const key = "auditLogs.activityType." + type;
            const t = this.$t(key);
            return t === key ? type : t;
        },
        eventSummary(row) {
            const t = row.type;
            const p = row.payload || {};
            if (t === "site-enabled") {
                return p.isEnabled === false
                    ? this.$t("auto.adminSiteAuditEventEnabledOff")
                    : this.$t("auto.adminSiteAuditEventEnabledOn");
            }
            if (t === "site-suspended") {
                return p.suspendStatus === true
                    ? this.$t("auto.adminSiteAuditEventSuspended")
                    : this.$t("auto.adminSiteAuditEventUnsuspended");
            }
            if (t === "site-dead") {
                return p.isDead === true
                    ? this.$t("auto.adminSiteAuditEventDeadMarked")
                    : this.$t("auto.adminSiteAuditEventDeadCleared");
            }
            return this.typeLabel(t);
        },
        summaryTagColor(row) {
            const t = row.type;
            const p = row.payload || {};
            if (t === "site-enabled") {
                // "default" renkli Tag bazı temalarda açık zemin + açık yazı veriyor; okunabilirlik için somut renk kullan.
                return p.isEnabled === false ? "orange" : "green";
            }
            if (t === "site-suspended") {
                return p.suspendStatus === true ? "orange" : "cyan";
            }
            if (t === "site-dead") {
                return p.isDead === true ? "red" : "blue";
            }
            return "blue";
        },
        descriptionText(row) {
            const p = row.payload || {};
            const t = row.type;
            if (t === "site-dead" && p.clearedWithUnsuspend === true) {
                return this.$t("auto.adminSiteAuditDescDeadUnsuspendClear");
            }
            if (t === "site-suspended") {
                if (p.suspendStatus === true && p.suspendReason) {
                    return this.$t("auto.adminSiteAuditDescSuspendedReason", { reason: p.suspendReason });
                }
                if (p.suspendStatus === false) {
                    return this.$t("auto.adminSiteAuditDescUnsuspend");
                }
            }
            if (t === "site-enabled") {
                return p.isEnabled === false
                    ? this.$t("auto.adminSiteAuditDescEnableOff")
                    : this.$t("auto.adminSiteAuditDescEnableOn");
            }
            if (t === "site-dead") {
                return p.isDead === true
                    ? this.$t("auto.adminSiteAuditDescDeadOn")
                    : this.$t("auto.adminSiteAuditDescDeadOffGeneric");
            }
            return "—";
        },
        formatDate(ts) {
            return moment(ts).format("DD.MM.YYYY");
        },
        formatTime(ts) {
            return moment(ts).format("HH:mm:ss");
        },
        actorLabel(row) {
            const actor = row.actor;
            if (actor === "user") {
                return _.get(row, "actorUser.name", "—");
            }
            if (actor === "admin-user") {
                const name = _.get(row, "actorUser.name");
                const email = _.get(row, "actorUser.email");
                if (name && email) {
                    return name + " (" + email + ")";
                }
                if (name) {
                    return name;
                }
                if (email) {
                    return email;
                }
                return "—";
            }
            if (actor === "visitor") {
                return _.get(row, "refVisitor.fullname", "—");
            }
            if (actor === "chatbot") {
                return "Chatbot";
            }
            if (actor === "system") {
                return this.$t("auditLogs.actorSystem");
            }
            return actor || "—";
        },
        resetAndFetch() {
            this.cursor.page = 1;
            this.fetchPage();
        },
        fetchPage() {
            this.loading = true;
            const filters = {
                activityTypes: SITE_STATUS_ACTIVITY_TYPES.slice(0),
            };
            getSiteAuditLog(this.siteId, this.cursor, filters)
                .then((res) => {
                    const items = (res.data && res.data.items) || [];
                    this.rows = items;
                    this.totalCount = (res.data && res.data.count) || 0;
                })
                .catch(() => {
                    this.$message.error(this.$t("auto.adminSiteAuditLogLoadError"));
                })
                .finally(() => {
                    this.loading = false;
                });
        },
    },
};
</script>

<style scoped lang="scss">
.admin-site-audit-log {
    padding: 0;
}

.admin-site-audit-log__panel {
    border-radius: 12px;
    border: 1px solid #e8eaed;
    background: #fff;
    box-shadow: 0 1px 3px rgba(18, 38, 63, 0.06);
    overflow: hidden;
}

.admin-site-audit-log__panel-head {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px 18px;
    background: linear-gradient(180deg, #fafbfc 0%, #f4f6f8 100%);
    border-bottom: 1px solid #e8eaed;
}

.admin-site-audit-log__head-icon {
    font-size: 22px;
    color: #46499d;
}

.admin-site-audit-log__head-title {
    font-size: 15px;
    font-weight: 600;
    color: #1f2937;
    letter-spacing: -0.02em;
}

.admin-site-audit-log__table {
    border: none;
}

.admin-site-audit-log__pager {
    padding: 12px 16px;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    border-top: 1px solid #f0f2f5;
    background: #fafbfc;
}

.cell-datetime {
    display: flex;
    flex-direction: column;
    gap: 2px;
    line-height: 1.35;
}

.cell-datetime__date {
    font-weight: 600;
    color: #1f2937;
    font-size: 13px;
}

.cell-datetime__time {
    font-size: 12px;
    color: #6b7280;
}

.cell-summary-tag {
    margin: 0;
    max-width: 100%;
    white-space: normal;
    line-height: 1.35;
}

.cell-description {
    margin: 0;
    font-size: 13px;
    line-height: 1.5;
    color: #374151;
}

.cell-actor {
    font-size: 13px;
    color: #111827;
    font-weight: 500;
}

.cell-empty {
    color: #9ca3af;
    font-size: 13px;
}
</style>
